import { AutoComplete, Button, Form, Input, Select, Upload } from "antd";
import Paragraph from "antd/lib/typography/Paragraph";
import {
  activeTemplatesSelector,
  phoneNumbersSelector,
} from "appredux/selectors";
import { useSelector } from "react-redux";
import { forwardRef, useCallback, useMemo } from "react";
import {
  colorsIcon,
  documentTemplateWAAccepted,
  imageTemplateWAAccepted,
  maxFileSizeDocumentTemplateAccepted,
  maxFileSizeImageTemplateAccepted,
} from "utils/static";
import { useTranslation } from "react-i18next";
import { filterOption, replaceTemplateBody } from "utils/helper";
import { Text } from "components/UI";
import { createUploadRequest } from "utils/uploadRequest";
import { UploadOutlined } from "@ant-design/icons";
import ButtonsPreview from "components/apps/Templates/ButtonsPreview";
import { useAntdMessage } from "hooks/useAntdMessage";
import { isEmpty } from "lodash-es";

/** Literal `\n` di string API → newline; lalu dipakai untuk preview HTML */
function normalizeTemplateRawContent(s) {
  return String(s ?? "").replace(/\\n/g, "\n");
}

// Meta WhatsApp API menolak nilai variable template yang mengandung newline
// (\n/\r), tab (\t), atau lebih dari 4 spasi berturut (error 132000).
// Regex deteksi (untuk warning) & fungsi pembersih (untuk normalize) dipakai
// bersama agar aturannya konsisten.
const DISALLOWED_TEMPLATE_PARAM = /[\r\n\t]| {5,}/;

function sanitizeTemplateParam(value) {
  if (typeof value !== "string") {
    return value;
  }
  // Newline & tab → 1 spasi, lalu rapikan spasi berlebih jadi maksimal 4.
  return value.replace(/[\r\n\t]+/g, " ").replace(/ {5,}/g, "    ");
}

const TemplateSelector = forwardRef(
  (
    {
      onSelect,
      templateBodyContainerClassName,
      selectProps = {},
      optionsParams = [],
      /** When set (including []), replaces Redux activeTemplatesSelector list */
      templates: templatesProp,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const reduxTemplates = useSelector(activeTemplatesSelector);
    const phoneNumbers = useSelector(phoneNumbersSelector);
    const templates =
      templatesProp !== undefined ? templatesProp : reduxTemplates;

    return (
      <>
        <Form.Item name="template_id" {...props}>
          <Select
            className="w-full"
            placeholder={t("select_template")}
            options={templates}
            allowClear
            fieldNames={{ label: "title", value: "id" }}
            onSelect={onSelect}
            filterOption
            optionFilterProp="title"
            showSearch
            ref={ref}
            {...selectProps}
          />
        </Form.Item>
        <Form.Item
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.template_id !== currentValues.template_id
          }
          noStyle
        >
          {({ getFieldValue }) => {
            const templateId = getFieldValue("template_id");
            if (!templateId) {
              return null;
            }
            const template = templates.find(
              (row) => Number(row.id) === Number(templateId)
            );
            const body = template?.payload?.body;
            const header = template?.payload?.header;
            const rawContent = normalizeTemplateRawContent(
              template?.payload?.content || ""
            );
            const previewHtmlNoBody = rawContent.replace(/\n/g, "<br />");
            // Tombol hanya ditampilkan untuk template channel inhouse (tanpa
            // waba_id); URL dinamisnya diisi lewat `template_button_param`.
            const channel = phoneNumbers?.find(
              (row) => Number(row.id) === Number(template?.phone_number_id),
            );
            const inhouseButtons =
              !!channel && !channel.waba_id
                ? template?.payload?.button?.buttons
                : undefined;
            const dynamicUrlButton = inhouseButtons?.find(
              (row) => row.type === "URL" && row.url?.includes("{{1}}"),
            );

            return (
              <div className={templateBodyContainerClassName}>
                <Paragraph className="flex-1">
                  {!isEmpty(body) ? (
                    <TemplateString templateString={rawContent} />
                  ) : (
                    <blockquote
                      dangerouslySetInnerHTML={{
                        __html: previewHtmlNoBody,
                      }}
                    />
                  )}
                  <ButtonsPreview
                    buttons={inhouseButtons}
                    className="mt-2 border border-gray-300 rounded-lg"
                  />
                </Paragraph>
                {!isEmpty(header) && <UploadAttachment />}
                {!isEmpty(body) && (
                  <TemplateBodyParams options={optionsParams} />
                )}
                {dynamicUrlButton && (
                  <TemplateButtonParam button={dynamicUrlButton} />
                )}
              </div>
            );
          }}
        </Form.Item>
        <Form.Item name="attachment_key" hidden noStyle>
          <Input />
        </Form.Item>
      </>
    );
  }
);
TemplateSelector.displayName = "TemplateSelector";

const TemplateString = ({ templateString = "" }) => {
  const form = Form.useFormInstance();
  const body = Form.useWatch("template_body", form);
  const htmlContent = useMemo(() => {
    if (isEmpty(body)) {
      return { __html: "" };
    }
    // Mengganti semua variabel dengan nilai yang sesuai
    const replacedString = replaceTemplateBody({ templateString, body });
    const withLineBreaks = replacedString.replace(/\n/g, "<br />");

    return { __html: withLineBreaks };
  }, [body, templateString]);
  return <blockquote dangerouslySetInnerHTML={htmlContent} />;
};

const TemplateBodyParams = ({ options }) => {
  const { t } = useTranslation();
  const { message } = useAntdMessage();

  // Blok Enter supaya user tidak bisa membuat baris baru + beri tahu alasannya
  // agar tidak kaget saat mengetik.
  const blockNewlineKey = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        message.warning(t("warning.wa_param_no_newline"));
      }
    },
    [message, t]
  );

  // Saat paste terdeteksi mengandung newline/tab/spasi berlebih, beri tahu user
  // bahwa teksnya akan disesuaikan (pembersihannya sendiri oleh `normalize`).
  const handlePaste = useCallback(
    (e) => {
      const text = e.clipboardData?.getData("text") ?? "";
      if (DISALLOWED_TEMPLATE_PARAM.test(text)) {
        message.warning(t("warning.wa_param_adjusted"));
      }
    },
    [message, t]
  );

  // Kontrol input variabel: TextArea multiline yang auto-wrap & tumbuh ke bawah
  // (autoSize) supaya kalimat panjang tetap kelihatan tanpa scroll ke samping.
  // Untuk mode blast (options terisi) TextArea dibungkus AutoComplete.
  const renderControl = useCallback(() => {
    const textAreaProps = {
      className: "w-full",
      autoSize: { minRows: 1, maxRows: 6 },
      style: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
      onPaste: handlePaste,
    };

    if (!isEmpty(options)) {
      // Di AutoComplete, Enter dipakai untuk memilih saran, jadi tidak diblok
      // di sini — newline dari paste sudah dibersihkan lewat `normalize`.
      return (
        <AutoComplete
          className="w-full"
          allowClear
          options={options}
          filterOption={filterOption}
        >
          <Input.TextArea {...textAreaProps} />
        </AutoComplete>
      );
    }

    return <Input.TextArea {...textAreaProps} onKeyDown={blockNewlineKey} />;
  }, [options, handlePaste, blockNewlineKey]);

  return (
    <Form.List name="template_body">
      {(fields) => (
        <div id="list_template_body" className="flex-1 space-y-4">
          {fields.map(({ key, name, ...restField }) => {
            return (
              <div key={key} className="flex items-stretch">
                {/* Badge {{n}} — tinggi mengikuti input, teks rata tengah vertikal */}
                <div
                  className="flex items-center justify-center"
                  style={{
                    padding: "0 11px",
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                    border: "1px solid #d9d9d9",
                    borderRight: 0,
                    borderTopLeftRadius: 6,
                    borderBottomLeftRadius: 6,
                  }}
                >
                  <Text
                    className="font-bold"
                    style={{ color: colorsIcon[name], whiteSpace: "nowrap" }}
                  >{`{{${name + 1}}}`}</Text>
                </div>
                <div className="flex-1">
                  <Form.Item
                    {...restField}
                    name={[name, "value"]}
                    noStyle
                    normalize={sanitizeTemplateParam}
                  >
                    {renderControl()}
                  </Form.Item>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Form.List>
  );
};

export default TemplateSelector;

const TemplateButtonParam = ({ button }) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const value = Form.useWatch("template_button_param", form);

  return (
    <Form.Item
      name="template_button_param"
      label={t("template.button_param", { value: button.text })}
      labelCol={{ span: 24, style: { paddingBottom: "3px" } }}
      wrapperCol={{ span: 24 }}
      className="mt-4"
      // Ganti template = buang nilai lama supaya tidak ikut terkirim.
      preserve={false}
      extra={
        <span className="break-all">
          {button.url.replace("{{1}}", value || "{{1}}")}
        </span>
      }
      rules={[
        {
          required: true,
          whitespace: true,
          message: t("template.button_param_required"),
        },
      ]}
    >
      <Input />
    </Form.Item>
  );
};

const uploadMediaRequest = createUploadRequest("/WA/medias");

const UploadAttachment = () => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  const attachmentValue = Form.useWatch("attachment");
  const attachmentKey = Form.useWatch("attachment_key");
  const attachmentFile = !isEmpty(attachmentValue);

  const beforeUploadHandler = (file) => {
    if (attachmentKey === "image") {
      if (!imageTemplateWAAccepted.includes(file.type)) {
        message.error(t("warning.unsupported_file_type_image"));
        return Upload.LIST_IGNORE;
      }
      if (file.size / 1024 / 1024 > maxFileSizeImageTemplateAccepted) {
        message.error(
          t("warning.file_size_limit", {
            value: maxFileSizeImageTemplateAccepted,
            type: "image",
          })
        );
        return Upload.LIST_IGNORE;
      }
    }

    if (attachmentKey === "document") {
      if (!documentTemplateWAAccepted.includes(file.type)) {
        message.error(t("warning.unsupported_file_type_docs"));
        return Upload.LIST_IGNORE;
      }
      if (file.size / 1024 / 1024 > maxFileSizeDocumentTemplateAccepted) {
        message.error(
          t("warning.file_size_limit", {
            value: maxFileSizeDocumentTemplateAccepted,
            type: "document",
          })
        );
        return Upload.LIST_IGNORE;
      }
    }

    return true;
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }

    if (e.file.status === "done") {
      const info = e?.fileList[0];
      message.success(t("success.upload_file", { value: info.name }));
      let key = "";

      if (imageTemplateWAAccepted.includes(info.type)) {
        key = "image";
      }
      if (documentTemplateWAAccepted.includes(info.type)) {
        key = "document";
      }
      const customValue = {
        key,
        name: info.name,
        metaId: info?.response?.data,
        media_document_filename: info.name,
      };
      return [customValue];
    } else if (e.file.status === "error") {
      message.error(t("warning.all_media"));
      return [];
    } else {
      return e?.fileList;
    }
  };

  return (
    <Form.Item
      name="attachment"
      label={
        attachmentKey === "image" ? t("attachment.image") : t("attachment.docs")
      }
      valuePropName="fileList"
      getValueFromEvent={normFile}
      labelCol={{
        span: 24,
        style: { paddingBottom: "3px" },
      }}
      wrapperCol={{
        span: 24,
      }}
      required
      rules={[
        {
          required: true,
          message: t("warning.required_upload", { value: attachmentKey }),
        },
      ]}
    >
      <Upload
        name="file"
        accept={
          attachmentKey === "image"
            ? imageTemplateWAAccepted.join(",")
            : documentTemplateWAAccepted.join(",")
        }
        customRequest={uploadMediaRequest}
        maxCount={1}
        beforeUpload={beforeUploadHandler}
      >
        {!attachmentFile && (
          <Button icon={<UploadOutlined />}>{t("btn.upload")}</Button>
        )}
      </Upload>
    </Form.Item>
  );
};
